/**
 * `person` — `PersonValue`, the **deprecated** single-person column
 * (`specs/M2-tier1-column-types.md` §5.7, `[09§A.7 deprecated]`, `[cvs§2 person]`).
 *
 * `value` is just `{"id": N}` — no `changed_at`. It is still writable through
 * `{"person": {"personsAndTeams": [{"id": 1234567, "kind": "person"}]}}`, but *"New person
 * columns cannot be created via the API"*, so the add-column gallery does not offer it either.
 *
 * **`toText` returns `""` even when the cell is populated.** monday's own `text` was empty on 74
 * of the 99 populated cells sampled — *"never use `text` for this type"*. We reproduce that on
 * `/v2` byte-for-byte while the cell renderer resolves and shows the name (open question Q67).
 *
 * monday's enum carries this one column under **both** `person` and `item_assignees`, and the
 * docs table and the enum disagree; the registry aliases the second name to this definition.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql, sqlFragment } from '../sql.js';
import { peopleSettingsSchema, readPeopleList } from '../people/index.js';
import { ColumnValueError, defineColumnType } from '../types.js';
import { personFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const personValueSchema = z.looseObject({ id: z.number().int() });
function coerce(raw) {
    const list = readPeopleList(raw, 'person');
    if (list === null || list.length === 0)
        return null;
    if (list.length > 1) {
        throw new ColumnValueError('person', 'the deprecated person column holds a single person');
    }
    const [ref] = list;
    if (ref === undefined)
        return null;
    if (ref.kind !== 'person') {
        throw new ColumnValueError('person', 'the deprecated person column cannot hold a team');
    }
    return { id: ref.id };
}
export const personColumn = defineColumnType({
    type: 'person',
    title: 'Person (deprecated)',
    description: 'A single person. Replaced by the People column; kept for migrated boards.',
    apiFragmentName: 'PersonValue',
    legacyTypeName: 'person',
    idPrefix: 'person',
    valueSchema: personValueSchema,
    settingsSchema: peopleSettingsSchema,
    defaultSettings: { max_people_allowed: '1' },
    settingsStrSchema: peopleSettingsSchema,
    writable: true,
    supportsSimpleValue: true,
    clearForms: ['null', 'empty-string'],
    filterConditions: personFilterConditions,
    filterOperators: operatorsOf(personFilterConditions),
    aggregations: ['avatars', 'count_filled', 'count_empty', 'none'],
    /** P-027: `90397660` `Owner` — monday's footer cell is **empty**. */
    defaultAggregation: 'none',
    emptyText: '',
    parseValue(raw) {
        return coerce(raw);
    },
    parseSimpleValue(raw) {
        return coerce(raw);
    },
    toText() {
        // Byte-matching monday: empty even when `person_id` is set `[cvs§2 person]`.
        return '';
    },
    toSortKeys(value, _settings, ctx) {
        if (value === null)
            return { text: null, number: null, date: null };
        // The shadow may resolve the name even though `text` does not — sorting a column of empty
        // strings would be useless, and the shadow is ours, not monday's wire format.
        const name = ctx?.resolveName?.({ id: value.id, kind: 'person' });
        return { text: name === undefined || name === '' ? null : name, number: 1, date: null };
    },
    isEqual(a, b) {
        return (a?.id ?? null) === (b?.id ?? null);
    },
    toActivityValue(value) {
        return value === null ? null : { id: value.id };
    },
    toAssignees(value) {
        return value === null ? [] : [{ id: value.id, kind: 'person' }];
    },
    fromMondayValue(raw) {
        return coerce(raw);
    },
    toMondayValue(value) {
        return value === null ? null : { id: value.id };
    },
    toSimpleValue(value) {
        return value === null ? '' : String(value.id);
    },
    fromMondaySettings(raw) {
        return peopleSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        if (value === null)
            return {};
        // `updated_at` was null on all 290 sampled cells `[cvs§2 person]`.
        return { person_id: String(value.id), updated_at: null };
    },
    /**
     * No webhook recording for this type: the conservative pass-through is monday's own `value`
     * object `[whk§7]`, which is what `toMondayValue` already produces (q330).
     */
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    bucketsOf(value, _settings, ctx) {
        if (!value)
            return [];
        return [{ key: value.id, label: ctx?.nameOf?.(value.id) ?? `#${String(value.id)}` }];
    },
    filterSql(ctx) {
        const cell = ctx.refs?.cell ?? 'cv';
        switch (ctx.operator) {
            case 'is_empty':
                return sqlFragment(`${cell}.item_id IS NULL`);
            case 'is_not_empty':
                return sqlFragment(`${cell}.item_id IS NOT NULL`);
            case 'any_of':
                return sqlFragment(`${cell}.value->>'id' = ANY(?::text[])`, [
                    ctx.compareValues.map((value) => String(value)),
                ]);
            case 'not_any_of':
                return sqlFragment(`(${cell}.item_id IS NULL OR NOT (${cell}.value->>'id' = ANY(?::text[])))`, [
                    ctx.compareValues.map((value) => String(value)),
                ]);
            default:
                // The three text rows compare the rendered name (see `people`).
                return defaultFilterSql('text', ctx);
        }
    },
    summarySql(ctx) {
        if (ctx.aggregation === 'avatars') {
            const cell = ctx.refs?.cell ?? 'cv';
            return {
                text: `${cell}.value->>'id' AS id, 'person' AS kind, count(*)`,
                params: [],
                shape: 'buckets',
            };
        }
        return defaultSummarySql(ctx);
    },
});
/** monday's own enum name for the same column `[09§A.7 deprecated]`. */
export const PERSON_ALIAS_TYPE = 'item_assignees';
//# sourceMappingURL=index.js.map